/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { useSelector } from 'react-redux';
import Conversation from './Conversation';
import ToolBar from './ToolBar';
import { isMobile } from '@/utils/utils';
import style from './index.module.less';
import CompanionAvatar from '@/components/CompanionAvatar';
import ResizableStage from '@/components/ResizableStage';
import BackgroundVideo from '@/components/BackgroundVideo';
import { useBackgroundVideoSound } from '@/lib/useBackgroundVideoSound';
import ModeSwitch from '@/components/ModeSwitch';
import { RootState } from '@/store';
import MobileToolBar from '@/pages/Mobile/MobileToolBar';
import CameraArea from './CameraArea';
import { useScene } from '@/lib/useCommon';
import RtcClient from '@/lib/RtcClient';

type CameraPanelRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type CameraInteraction =
  | {
      type: 'drag';
      pointerStartX: number;
      pointerStartY: number;
      startRect: CameraPanelRect;
    }
  | {
      type: 'resize';
      pointerStartX: number;
      pointerStartY: number;
      startRect: CameraPanelRect;
    };

const CAMERA_PANEL_RATIO = 264 / 184;
const CAMERA_PANEL_MIN_WIDTH = 180;
const CAMERA_PANEL_MAX_WIDTH = 420;
const CAMERA_PANEL_OFFSET = 18;
const CAMERA_PANEL_DEFAULT_WIDTH = 264;
const CAMERA_PANEL_MOBILE_OFFSET = 14;
const CAMERA_PANEL_MOBILE_DEFAULT_WIDTH = 188;

// localStorage keys for the in-call companion / subtitle sizing (drag-to-resize).
const PET_SCALE_STORAGE_KEY = 'voxie_immersive_pet_scale_v1';
const PET_POS_STORAGE_KEY = 'voxie_immersive_pet_pos_v1';
const MOBILE_PET_SCALE_STORAGE_KEY = 'voxie_pet_scale_mobile_v1';
const BUBBLE_SCALE_STORAGE_KEY = 'voxie_bubble_scale_v1';
const DESKTOP_BUBBLE_SCALE_STORAGE_KEY = 'voxie_desktop_bubble_scale_v1';
const DESKTOP_AI_BUBBLE_POS_STORAGE_KEY = 'voxie_immersive_ai_bubble_pos_v1';
const DESKTOP_USER_BUBBLE_SCALE_STORAGE_KEY = 'voxie_desktop_user_bubble_scale_v1';
const DESKTOP_USER_BUBBLE_POS_STORAGE_KEY = 'voxie_immersive_user_bubble_pos_v1';

function Room() {
  const room = useSelector((state: RootState) => state.room);
  const { botName, isVision, isScreenMode, name } = useScene();
  const { isShowSubtitle, interactionMode, isJoined, isAIGCEnable, localUser, remoteUsers } = room;
  const {
    avatarBgUrl: bgImage = '',
    avatarBgVideoUrl: bgVideo = '',
    icon: avatarIcon = '',
    portraitFrames,
    live2dModelUrl,
  } = useScene();
  const mobile = isMobile();
  // Video backgrounds play on desktop only; mobile keeps the static image to
  // avoid autoplay/perf/bandwidth pitfalls (matches the mobile-fallback policy).
  const showBackgroundVideo = !mobile && Boolean(bgVideo);
  const [bgSoundOn] = useBackgroundVideoSound();
  const sceneName = name || botName || 'Voxie';
  const mobileStatusText = room.isAITalking
    ? '正在回答你'
    : room.isAIThinking
    ? '正在思考中'
    : '随时可以开始聊';
  // Companion driving signal: reuse the same RTC volume pipeline for lip-sync.
  // The agent's RTC identity is not guaranteed to equal `botName` on `username`
  // (audio reports merge by `userId`, and the agent may join as a `voiceChat_`
  // user), so match the same 3 ways the rest of the app resolves the agent.
  const getRemoteVolume = (user: (typeof remoteUsers)[number]) =>
    user.audioPropertiesInfo?.linearVolume || 0;
  const isAgentRemoteUser = (user: (typeof remoteUsers)[number]) =>
    user.username === botName ||
    user.userId === botName ||
    user.rtcUserId === botName ||
    Boolean(user.userId && user.userId.includes('voiceChat_')) ||
    Boolean(user.userId && user.userId.includes('RobotMan_')) ||
    Boolean(user.rtcUserId && user.rtcUserId.includes('voiceChat_')) ||
    Boolean(user.rtcUserId && user.rtcUserId.includes('RobotMan_'));
  const agentRemoteUser = remoteUsers
    .filter(isAgentRemoteUser)
    .sort((a, b) => getRemoteVolume(b) - getRemoteVolume(a))[0];
  const fallbackSpeakingRemoteUser = room.isAITalking
    ? [...remoteUsers].sort((a, b) => getRemoteVolume(b) - getRemoteVolume(a))[0]
    : undefined;
  const botVolume = agentRemoteUser
    ? getRemoteVolume(agentRemoteUser)
    : fallbackSpeakingRemoteUser
    ? getRemoteVolume(fallbackSpeakingRemoteUser)
    : 0;
  const [cameraPanelRect, setCameraPanelRect] = useState<CameraPanelRect>({
    x: CAMERA_PANEL_OFFSET,
    y: CAMERA_PANEL_OFFSET,
    width: CAMERA_PANEL_DEFAULT_WIDTH,
    height: Math.round(CAMERA_PANEL_DEFAULT_WIDTH / CAMERA_PANEL_RATIO),
  });
  const roomRef = useRef<HTMLDivElement | null>(null);
  const cameraPanelRectRef = useRef(cameraPanelRect);
  const cameraInteractionRef = useRef<CameraInteraction | null>(null);
  // Rendered character bounding box (fractions of the stage), used to hug the
  // resize handle to the visible art instead of the empty letterbox corner.
  const [avatarRect, setAvatarRect] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const agentUserIds = useMemo(
    () =>
      remoteUsers
        .flatMap(({ userId, username, rtcUserId }) => [userId, username, rtcUserId])
        .filter((userId): userId is string => Boolean(userId))
        .filter(
          (userId) =>
            userId === botName || userId.includes('voiceChat_') || userId.includes('RobotMan_')
        ),
    [botName, remoteUsers]
  );
  const agentUserIdsKey = agentUserIds.join('|');

  useEffect(() => {
    const currentAgentUserIds = agentUserIdsKey ? agentUserIdsKey.split('|').filter(Boolean) : [];
    if (!isJoined || !currentAgentUserIds.length) return;
    const shouldPauseAgentPlayback =
      interactionMode === 'smart_assistant' && (!isAIGCEnable || !localUser.publishAudio);
    const playbackVolume = shouldPauseAgentPlayback ? 0 : 100;
    currentAgentUserIds.forEach((agentUserId) => {
      RtcClient.setRemoteAudioPlaybackVolume(agentUserId, playbackVolume);
    });
  }, [agentUserIdsKey, interactionMode, isAIGCEnable, isJoined, localUser.publishAudio]);

  const showCameraPanel =
    interactionMode !== 'smart_assistant' &&
    isJoined &&
    isVision &&
    localUser.publishVideo &&
    !isScreenMode;

  const clampCameraPanelRect = (nextRect: CameraPanelRect) => {
    const panel = roomRef.current;
    if (!panel) return nextRect;
    const panelWidth = panel.clientWidth;
    const panelHeight = panel.clientHeight;
    const maxWidth = Math.min(CAMERA_PANEL_MAX_WIDTH, Math.max(CAMERA_PANEL_MIN_WIDTH, panelWidth));
    const width = Math.min(Math.max(nextRect.width, CAMERA_PANEL_MIN_WIDTH), maxWidth);
    const height = Math.round(width / CAMERA_PANEL_RATIO);
    const maxX = Math.max(0, panelWidth - width);
    const maxY = Math.max(0, panelHeight - height);
    return {
      x: Math.min(Math.max(nextRect.x, 0), maxX),
      y: Math.min(Math.max(nextRect.y, 0), maxY),
      width,
      height,
    };
  };

  const updateCameraPanelRect = (nextRect: CameraPanelRect) => {
    setCameraPanelRect(clampCameraPanelRect(nextRect));
  };

  const getDefaultCameraPanelRect = (): CameraPanelRect => {
    const panel = roomRef.current;
    const offset = mobile ? CAMERA_PANEL_MOBILE_OFFSET : CAMERA_PANEL_OFFSET;
    const preferredWidth = mobile ? CAMERA_PANEL_MOBILE_DEFAULT_WIDTH : CAMERA_PANEL_DEFAULT_WIDTH;
    const fallbackRect = {
      x: offset,
      y: offset,
      width: preferredWidth,
      height: Math.round(preferredWidth / CAMERA_PANEL_RATIO),
    };
    if (!panel) return fallbackRect;
    const availableWidth = Math.max(CAMERA_PANEL_MIN_WIDTH, panel.clientWidth - offset * 2);
    const width = Math.min(
      CAMERA_PANEL_MAX_WIDTH,
      Math.max(CAMERA_PANEL_MIN_WIDTH, Math.min(preferredWidth, availableWidth))
    );
    const height = Math.round(width / CAMERA_PANEL_RATIO);
    return clampCameraPanelRect({
      x: panel.clientWidth - width - offset,
      y: offset,
      width,
      height,
    });
  };

  useEffect(() => {
    cameraPanelRectRef.current = cameraPanelRect;
  }, [cameraPanelRect]);

  useEffect(() => {
    if (!showCameraPanel) {
      cameraInteractionRef.current = null;
      return;
    }
    updateCameraPanelRect(getDefaultCameraPanelRect());
    const handleWindowResize = () => {
      updateCameraPanelRect(cameraPanelRectRef.current);
    };
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [mobile, showCameraPanel]);

  useEffect(() => {
    if (!showCameraPanel) return;
    const handleMouseMove = (event: MouseEvent) => {
      const interaction = cameraInteractionRef.current;
      if (!interaction) return;
      if (interaction.type === 'drag') {
        updateCameraPanelRect({
          ...interaction.startRect,
          x: interaction.startRect.x + (event.clientX - interaction.pointerStartX),
          y: interaction.startRect.y + (event.clientY - interaction.pointerStartY),
        });
        return;
      }
      const deltaWidth = Math.max(
        event.clientX - interaction.pointerStartX,
        (event.clientY - interaction.pointerStartY) * CAMERA_PANEL_RATIO
      );
      const width = interaction.startRect.width + deltaWidth;
      updateCameraPanelRect({
        ...interaction.startRect,
        width,
        height: Math.round(width / CAMERA_PANEL_RATIO),
      });
    };
    const handleMouseUp = () => {
      cameraInteractionRef.current = null;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [showCameraPanel]);

  const handleCameraPanelDragStart = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    cameraInteractionRef.current = {
      type: 'drag',
      pointerStartX: event.clientX,
      pointerStartY: event.clientY,
      startRect: cameraPanelRectRef.current,
    };
  };

  const handleCameraPanelResizeStart = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    cameraInteractionRef.current = {
      type: 'resize',
      pointerStartX: event.clientX,
      pointerStartY: event.clientY,
      startRect: cameraPanelRectRef.current,
    };
  };

  return (
    <div
      ref={roomRef}
      className={`${style.wrapper} ${mobile ? style.mobile : ''}`}
      style={bgImage ? { backgroundImage: `url(${bgImage})` } : undefined}
    >
      {showBackgroundVideo ? (
        <BackgroundVideo src={bgVideo} poster={bgImage} overlay="" sound={bgSoundOn} />
      ) : null}
      {!mobile ? (
        <ModeSwitch className={`${style.modeSwitch} ${style.inCallModeSwitch}`} compact />
      ) : null}
      {mobile ? <MobileToolBar sceneName={sceneName} statusText={mobileStatusText} /> : null}
      {mobile ? (
        <div className={style.mobileAvatarStage}>
          <ResizableStage
            storageKey={MOBILE_PET_SCALE_STORAGE_KEY}
            min={0.6}
            max={1.6}
            origin="center center"
            handleLabel="拖拽调整桌宠大小"
            handleRect={avatarRect}
            resizable={false}
          >
            <CompanionAvatar
              live2dModelUrl={live2dModelUrl}
              avatar={avatarIcon}
              portraitFrames={portraitFrames}
              isTalking={room.isAITalking}
              volume={botVolume}
              fit="contain"
              zoom={1}
              alt={sceneName}
              disableLive2d
              onContentRect={setAvatarRect}
            />
          </ResizableStage>
        </div>
      ) : null}
      {showCameraPanel ? (
        <div
          className={style.floatingCameraShell}
          style={{
            left: `${cameraPanelRect.x}px`,
            top: `${cameraPanelRect.y}px`,
            width: `${cameraPanelRect.width}px`,
            height: `${cameraPanelRect.height}px`,
          }}
        >
          <CameraArea className={style.focusCameraArea} fillContainer />
          <div
            onMouseDown={handleCameraPanelDragStart}
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              cursor: 'move',
              touchAction: 'none',
              background: 'rgba(0,0,0,0.001)',
            }}
          />
          {!mobile ? (
            <button
              type="button"
              className={style.floatingCameraResizeHandle}
              onMouseDown={handleCameraPanelResizeStart}
              aria-label="拖拽调整视频画面大小"
              title="拖拽调整大小"
            />
          ) : null}
        </div>
      ) : null}
      {mobile && isShowSubtitle ? (
        <div className={style.conversationResizer}>
          <ResizableStage
            storageKey={BUBBLE_SCALE_STORAGE_KEY}
            min={0.75}
            max={1.6}
            origin="bottom center"
            handleLabel="拖拽调整字幕大小"
            resizable={false}
          >
            <Conversation
              className={style.conversation}
              showSubtitle={isShowSubtitle}
              maxMessages={1}
              showLoadingIndicator={false}
              showInterruptedTag={false}
            />
          </ResizableStage>
        </div>
      ) : null}
      <ToolBar className={style.toolBar} />
      <div className={style.declare}>AI生成内容由大模型生成，不能完全保障真实</div>
      {!mobile ? (
        <div className={style.desktopAiSubtitle}>
          <ResizableStage
            storageKey={DESKTOP_BUBBLE_SCALE_STORAGE_KEY}
            offsetStorageKey={DESKTOP_AI_BUBBLE_POS_STORAGE_KEY}
            min={0.75}
            max={1.6}
            origin="top left"
            movable
            resizable={false}
            handleLabel="拖拽移动字幕位置"
          >
            <Conversation
              className={style.conversation}
              showSubtitle={isShowSubtitle}
              maxMessages={2}
              roleFilter="ai"
              showLoadingIndicator={false}
              showInterruptedTag={false}
            />
          </ResizableStage>
        </div>
      ) : null}
      {!mobile ? (
        <div className={style.desktopUserSubtitle}>
          <ResizableStage
            storageKey={DESKTOP_USER_BUBBLE_SCALE_STORAGE_KEY}
            offsetStorageKey={DESKTOP_USER_BUBBLE_POS_STORAGE_KEY}
            min={0.75}
            max={1.6}
            origin="bottom right"
            movable
            resizable={false}
            handleLabel="拖拽移动字幕位置"
          >
            <Conversation
              className={style.conversation}
              showSubtitle={isShowSubtitle}
              maxMessages={1}
              roleFilter="user"
              showLoadingIndicator={false}
              showInterruptedTag={false}
            />
          </ResizableStage>
        </div>
      ) : null}
      {!mobile ? (
        <div className={style.live2dStage}>
          <ResizableStage
            storageKey={PET_SCALE_STORAGE_KEY}
            offsetStorageKey={PET_POS_STORAGE_KEY}
            min={0.6}
            max={2.4}
            origin="bottom center"
            movable
            handleLabel="拖拽调整桌宠大小"
            handleRect={avatarRect}
          >
            <CompanionAvatar
              live2dModelUrl={live2dModelUrl}
              avatar={avatarIcon}
              portraitFrames={portraitFrames}
              isTalking={room.isAITalking}
              volume={botVolume}
              fit="contain"
              zoom={1}
              alt={sceneName}
              onContentRect={setAvatarRect}
            />
          </ResizableStage>
        </div>
      ) : null}
    </div>
  );
}

export default Room;
